import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Text } from '@mantine/core';
import { env } from '../../config/env';

/** The footer height before the first measure (16 px line plus padding). */
const DEFAULT_FOOTER_HEIGHT_PX = 26;

/**
 * AppVersion - Displays the application version for bug reporting.
 * MED-1360: the admin, coach and participant layouts render it in the
 * page flow, after all page content, at the bottom right. As a fixed label
 * it covered buttons and badges. It never adds a scroll: on a page that
 * exactly fills the window (Data Library, the participant dashboard while
 * it loads) it hides. Full-height screens outside these layouts (login,
 * waiting room, active session) do not render it.
 */
export function AppVersion() {
  const footerRef = useRef<HTMLElement>(null);
  const footerHeight = useRef(DEFAULT_FOOTER_HEIGHT_PX);
  const [hidden, setHidden] = useState(false);

  useLayoutEffect(() => {
    const root = document.getElementById('root');
    const check = () => {
      const footer = footerRef.current;
      if (!footer) return;
      if (footer.offsetHeight > 0) footerHeight.current = Math.ceil(footer.offsetHeight);
      const shownHeight = footer.offsetHeight > 0 ? footerHeight.current : 0;
      const pageHeight = document.documentElement.scrollHeight - shownHeight;
      // Hide only when the page fits the window without the footer but not with it.
      setHidden(pageHeight <= window.innerHeight && pageHeight + footerHeight.current > window.innerHeight);
    };
    check();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    if (root) observer?.observe(root);
    window.addEventListener('resize', check);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', check);
    };
  }, []);

  return (
    <Box
      ref={footerRef}
      component="footer"
      style={{
        display: hidden ? 'none' : 'block',
        padding: '4px 12px 6px',
        textAlign: 'right',
      }}
    >
      {/* MED-1360: the app's dimmed text token. #C5C3BE was too faint to read. */}
      <Text
        size="xs"
        c="dimmed"
        style={{
          fontSize: 11,
          fontWeight: 400,
          lineHeight: '16px',
          userSelect: 'text',
        }}
      >
        {env.versionDisplay}
      </Text>
    </Box>
  );
}
